import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import SuiBox from "components/SuiBox";

import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";
import SuiInput from "components/SuiInput";
import DraggableDialog from "components/UpStyled/DraggableDialog";
import SuiBadge from "components/SuiBadge";

import Card from "@mui/material/Card";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Tooltip from "@mui/material/Tooltip";

import api from "services/api";

import { AccountContext } from "context/account";
import { StoreContext } from "context/store";

import { toast } from "react-hot-toast";
import { CheckOutlined, SyncOutlined } from "@ant-design/icons";
import ReplayIcon from "@mui/icons-material/Replay";

const InviteDesigner = () => {
  const [sentStatus, setSentStatus] = useState(null);
  const [inputEmailValue, setInputEmailValue] = useState("");
  const [inviteList, setInviteList] = useState([]);

  const account = useContext(AccountContext);

  useEffect(() => {
    if (account.id) {
      handleListInvites();
    }
  }, [account.id]);

  const handleSendEmail = async () => {
    if (!inputEmailValue) {
      return toast.error("Please input an email address!");
    }
    var mailformat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (inputEmailValue.match(mailformat)) {
      try {
        await api.invites.create({
          user: inputEmailValue,

          account_type: "de",
          invited_by: account.id,
        });
        handleListInvites();

        setSentStatus("success");
        toast.success(`Send invitation email to ${inputEmailValue} successfully!`);
      } catch (err) {
        let message = `Send invitation email to ${inputEmailValue} failed`;
        if (err?.response?.data?.message) {
          message = err?.response?.data?.message;
        }
        toast.error(message);
      }
    } else {
      setSentStatus("error");
      toast.error("Not a valid email address!");
    }
  };

  const handleResend = async (inviteId) => {
    try {
      await api.invites.resend(inviteId);
      handleListInvites();

      toast.success(`Resend invitation email successfully!`);
    } catch (err) {
      let message = `Resend invitation email failed`;
      if (err?.response?.data?.message) {
        message = err?.response?.data?.message;
      }
      toast.error(message);
    }
  };

  const handleListInvites = async () => {
    try {
      const { data } = await api.invites.list({
        owner_id: account.id,
        account_type: "de",
      });
      setInviteList(data?.invites || []);
    } catch (err) {
      let message = `Could not retrieve invite list`;
      if (err?.response?.data?.message) {
        message = err?.response?.data?.message;
      }
      toast.error(message);
    }
  };

  const handleEmailChange = (e) => {
    let email = e?.target.value;
    var mailformat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    setInputEmailValue(email);
    if (email.match(mailformat)) {
      setSentStatus("success");
    } else {
      setSentStatus("error");
    }
  };

  const formatEmailSentStatus = () => {
    if (sentStatus == "success") {
      return <CheckCircleOutlineIcon color="success" fontSize="small" />;
    } else if (sentStatus == "error") {
      return <HighlightOffIcon color="error" fontSize="small" />;
    } else {
      return <div />;
    }
  };
  return (
    <Card id="invite-designer" sx={{ overflow: "visible" }}>
      <SuiBox p={3}>
        <SuiBox mb={1}>
          <SuiTypography variant="h5">Invite Friends</SuiTypography>{" "}
        </SuiBox>
        <SuiTypography variant="button" color="text" fontWeight="regular">
          Send an email to invite your friends
        </SuiTypography>
      </SuiBox>

      <SuiBox pb={3} px={3}>
        {inviteList.length > 0 ? (
          <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
            <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <SuiTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Invite List
              </SuiTypography>
            </SuiBox>
            <SuiBox pb={1} px={1}>
              {inviteList.map((u) => (
                <SuiBox display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <SuiTypography color="text">{u.user_email}</SuiTypography>
                  {u.accepted ? (
                    <SuiBox color="info">
                      <CheckOutlined style={{ marginRight: 2 }} />
                      Accepted
                    </SuiBox>
                  ) : (
                    <SuiBox color="info">
                      <SyncOutlined spin style={{ marginRight: 2 }} />
                      Pending
                      <Tooltip title="Resend invitation link">
                        <SuiButton
                          onClick={() => {
                            handleResend(u.id);
                          }}
                          color="dark"
                          sx={{
                            ml: 1,
                            padding: "0px 8px",
                            fontSize: 12,
                            lineHeight: 0,
                            minHeight: 25,
                            minWidth: 25,
                            textTransform: "none",
                            borderRadius: "4px",
                          }}
                          size="small"
                        >
                          Resend
                        </SuiButton>
                      </Tooltip>
                    </SuiBox>
                  )}
                </SuiBox>
              ))}
            </SuiBox>
          </SuiBox>
        ) : null}

        <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
            <SuiTypography
              component="label"
              variant="caption"
              fontWeight="bold"
              textTransform="capitalize"
            >
              invite your friends
            </SuiTypography>
          </SuiBox>
          <SuiBox pb={1} sx={{ maxWidth: 600 }}>
            <SuiInput
              type="email"
              placeholder="Email"
              onChange={handleEmailChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSendEmail(e);
                }
              }}
              icon={{ component: formatEmailSentStatus(), direction: "right" }}
            />
          </SuiBox>
        </SuiBox>

        <SuiBox display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap">
          <SuiBox ml="auto">
            <SuiButton variant="gradient" color="dark" onClick={handleSendEmail}>
              Invite
            </SuiButton>
          </SuiBox>
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default InviteDesigner;
